import { useNarrativesCommunityInfinite } from '../hooks/useNarrativesCommunityInfinite';
import { NarrativeCardLoading } from '@/components/NarrativeCard';
import {
  NARRATIVE_COMMUNITY_LIST_PAGE_SIZE,
  NARRATIVE_COMMUNITY_PRELOAD_OFFSET,
  handleOnSetPrimary,
  narrativeListPageParamsSafeParse,
} from '../utils';
import EyeSolid from '@simplywallst/ui-core/icons/EyeSolid';
import { NarrativesEmptyPanel } from '../../NarrativesEmptyPanel';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import {
  FUTURE_NARRATIVE_ID,
  useNarrativeCommunityQueryParamCursors,
} from '../hooks/useNarrativeCommunityQueryParamCursors';
import { useNarrativeCommunityGoToStart } from '../hooks/useNarrativeCommunityGoToStart';
import { useTracking } from 'react-tracking';
import { useCallback, useEffect } from 'react';
import { cn } from '@simplywallst/ui-core/next';
import { NarrativeCommunityListDocumentHeader } from './NarrativeCommunityListDocumentHeader';
import { ROUTE_NARRATIVE_COMMUNITY } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { NarrativeCommunityListPaginationButton } from './NarrativeCommunityListPaginationButton';
import { useNarrativeCommunitySort } from '../../NarrativeCommunity/hooks/useNarrativeCommunitySort';
import isEqual from 'lodash/isEqual';
import { usePreviousObjectValue } from '../hooks/usePreviousObjectValue';
import { NarrativeCommunityListLayout } from './NarrativeCommunityListLayout';
import { NarrativeInvalidationBoundary } from '../../NarrativeInvalidationBoundary/NarrativeInvalidationBoundary';
import { NarrativeCommunityListItem } from './NarrativeCommunityListItem';

type Props = {
  filter: Parameters<typeof useNarrativesCommunityInfinite>[0]['filter'];
  sort: Parameters<typeof useNarrativesCommunityInfinite>[0]['sort'];
  seoTitle: string;
  seoDescription: string;
  seoImageUrl: string;
};

export const NarrativeCommunityListMain = ({
  filter,
  sort,
  seoTitle,
  seoDescription,
  seoImageUrl,
}: Props) => {
  const { trackEvent, Track } = useTracking({
    subject: 'listing',
  });
  const {
    updateNarrativeVisibility,
    resetNarrativeVisibility,
    initialBefore,
    initialAfter,
  } = useNarrativeCommunityQueryParamCursors();

  const previousSort = usePreviousObjectValue(sort);
  const { forwardSortHash } = useNarrativeCommunitySort();

  /**
   * Reset initial cursor if sort field or direction is different from the previous one so that we start at the beginning
   */
  const isNewSort = !isEqual(sort ?? null, previousSort ?? null);
  const variables = {
    first: NARRATIVE_COMMUNITY_LIST_PAGE_SIZE,
    last: NARRATIVE_COMMUNITY_LIST_PAGE_SIZE,
    after: isNewSort ? null : initialAfter,
    before: isNewSort ? null : initialBefore,
    filter,
    sort,
  } satisfies Parameters<typeof useNarrativesCommunityInfinite>['0'];
  const {
    data: narratives,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useNarrativesCommunityInfinite(variables);

  const firstPage = narratives?.pages[0].narratives;
  const lastPage = narratives?.pages.at(-1)?.narratives;
  const isLoading = isFetchingNextPage || isFetchingPreviousPage;

  const handleFetchNextPage = useCallback(async () => {
    if (!hasNextPage || isLoading) return;

    await fetchNextPage();
    trackEvent({
      modifier: 'next_page',
      action: 'scroll',
      pageIndex: narratives?.pages.length,
    });
  }, [hasNextPage, isLoading, fetchNextPage, trackEvent, narratives]);

  useNarrativeCommunityGoToStart({
    hasFirstPage: firstPage?.pageInfo.hasPreviousPage === false,
  });

  useEffect(() => {
    resetNarrativeVisibility();
  }, [
    resetNarrativeVisibility,
    sort?.field,
    sort?.direction,
    filter?.canonicalUrl,
    filter?.market,
    filter?.primaryIndustryId,
  ]);

  if (!narratives || !narratives.pages.length || !firstPage.edges.length)
    return (
      <NarrativesEmptyPanel>
        There are no community narratives available.
      </NarrativesEmptyPanel>
    );

  return (
    <Track>
      <NarrativeCommunityListDocumentHeader
        narratives={narratives}
        seoTitle={seoTitle}
        seoDescription={seoDescription}
        seoImageUrl={seoImageUrl}
      />
      <div className="grid gap-x2">
        {hasPreviousPage && (
          <div className="grid grid-cols-[minmax(0,300px)] place-content-center">
            <NarrativeCommunityListPaginationButton
              isDisabled={isLoading}
              isLoading={isFetchingPreviousPage}
              to={forwardSortHash(
                getPath({
                  type: ROUTE_NARRATIVE_COMMUNITY,
                  query: {
                    before: firstPage.pageInfo.startCursor ?? undefined,
                  },
                  payload: {},
                })
              )}
              onClick={() => {
                fetchPreviousPage();
                trackEvent({
                  modifier: 'prev_page_btn',
                  action: 'click',
                });
              }}
              rel="nofollow"
            >
              <EyeSolid className="fill-current" />
              Load previous narratives
            </NarrativeCommunityListPaginationButton>
          </div>
        )}
        <NarrativeInvalidationBoundary
          onSetPrimary={handleOnSetPrimary(
            useNarrativesCommunityInfinite.getOptions(variables).queryKey
          )}
        >
          <NarrativeCommunityListLayout role="list">
            {narratives.pages.map((page, pageIndex) =>
              page.narratives.edges.map(({ node: narrative }) => {
                const currentPageParam = narrativeListPageParamsSafeParse(
                  narratives.pageParams[pageIndex]
                );
                return (
                  <NarrativeCommunityListItem
                    key={narrative.id}
                    narrative={narrative}
                    hasPreviousPage={page.narratives.pageInfo.hasPreviousPage}
                    after={currentPageParam.after}
                    before={currentPageParam.before}
                    updateNarrativeVisibility={updateNarrativeVisibility}
                  />
                );
              })
            )}
            {Array.from({ length: NARRATIVE_COMMUNITY_LIST_PAGE_SIZE }).map(
              (_, index) => (
                <IntersectionObserver
                  key={index}
                  threshold={0}
                  onChange={(entry) => {
                    // When user scrolled to the bottom of the page, in order to keep the query params
                    // pointing to the last page, we manually set the after cursor to the last end cursor
                    updateNarrativeVisibility({
                      narrativeId: FUTURE_NARRATIVE_ID,
                      isVisible: entry.isIntersecting,
                      after: lastPage?.pageInfo.endCursor,
                      before: null,
                    });
                  }}
                >
                  <div className={cn(isFetchingNextPage ? 'block' : 'hidden')}>
                    <NarrativeCardLoading showCompanyIcon />
                  </div>
                </IntersectionObserver>
              )
            )}
          </NarrativeCommunityListLayout>
        </NarrativeInvalidationBoundary>

        {hasNextPage && !isFetchingNextPage && (
          <IntersectionObserver
            rootMargin={`${NARRATIVE_COMMUNITY_PRELOAD_OFFSET}px`}
            threshold={0}
            onChange={(entry) => {
              if (entry.isIntersecting) handleFetchNextPage();
            }}
          >
            <footer
              aria-label="Load more narratives threshold"
              className="grid grid-cols-[minmax(0,300px)] place-content-center"
            >
              <NarrativeCommunityListPaginationButton
                isLoading={isFetchingNextPage}
                isDisabled={isLoading}
                to={forwardSortHash(
                  getPath({
                    type: ROUTE_NARRATIVE_COMMUNITY,
                    query: {
                      after: lastPage?.pageInfo.endCursor ?? undefined,
                    },
                    payload: {},
                  })
                )}
                onClick={() => {
                  fetchNextPage();
                  trackEvent({
                    modifier: 'next_page_btn',
                    action: 'click',
                  });
                }}
              >
                <EyeSolid className="fill-current" />
                Load more narratives
              </NarrativeCommunityListPaginationButton>
            </footer>
          </IntersectionObserver>
        )}
      </div>
    </Track>
  );
};
